import {
  BillingPaymentScheduleCalendar,
  SettlementType,
} from "@/models/billingPaymentSchedule";
import { formatDateJA, getLocalDateFromDateStr } from "@/util/datetime";
import {
  BillingPaymentScheduleConstants,
  settlementLabel,
  settlementLabelAbbr,
} from "./BillingPaymentScheduleConstants";

export type EventType =
  | "invoice"
  | "settlement"
  | "paymentNotice"
  | "outsourcingFeePayment";

export type ScheduleCalendarEvent = {
  // カレンダー枠内に表示する短縮名称
  title: string;
  // ホバー時に表示する正式名称
  fullTitle: string;
  start: Date;
  end: Date;
  allDay: boolean;
  type: EventType;
};

// 日付の種類ごとの色分け（カテゴリカルパレットの固定順に対応させる。並び替えない）
export const EVENT_COLORS: Record<
  EventType,
  { background: string; text: string }
> = {
  invoice: { background: "#2a78d6", text: "#ffffff" },
  settlement: { background: "#eb6834", text: "#ffffff" },
  paymentNotice: { background: "#1baf7a", text: "#ffffff" },
  outsourcingFeePayment: { background: "#eda100", text: "#1a1a1a" },
};

// カレンダー凡例1件分（日付項目の色・カレンダー枠内と同じ短縮表記・正式名称の対応。祝日の色分けは含めない）
export type LegendItem = {
  type: EventType;
  // カレンダー枠内の表示と同じ短縮名称（色付きチップ内に表示する）
  abbr: string;
  label: string;
};

/**
 * カレンダー下部に表示する凡例一覧を組み立てる.
 *
 * 決済方法（口座振替/銀行振込）は営業所によっていずれか一方のみ表示されるため、
 * 実際に表示中のsettlementTypeに応じた凡例ラベルにする。settlementTypeが未確定
 * （未公開の年月を表示中、または取得中）の場合、実際には銀行振込の可能性もあるため
 * 口座振替と決め打ちせず、決済方法の項目自体を凡例から省く。
 */
export const buildLegendItems = (
  settlementType: SettlementType | null
): LegendItem[] => [
  {
    type: "invoice",
    abbr: BillingPaymentScheduleConstants.InvoiceSendOnAbbr,
    label: BillingPaymentScheduleConstants.InvoiceSendOn,
  },
  ...(settlementType
    ? [
        {
          type: "settlement" as const,
          abbr: settlementLabelAbbr(settlementType),
          label: settlementLabel(settlementType),
        },
      ]
    : []),
  {
    type: "paymentNotice",
    abbr: BillingPaymentScheduleConstants.PaymentNoticeSendOnAbbr,
    label: BillingPaymentScheduleConstants.PaymentNoticeSendOn,
  },
  {
    type: "outsourcingFeePayment",
    abbr: BillingPaymentScheduleConstants.OutsourcingFeePaymentOnAbbr,
    label: BillingPaymentScheduleConstants.OutsourcingFeePaymentOn,
  },
];

// startとendが同じ0:00のDateだと、react-big-calendarが終日イベントを長さ0として扱い
// 表示されなくなる（既存のChargePlans.tsx等と同じOSS側の挙動）。endを1ms進めて回避する。
const toEventEnd = (date: Date): Date => {
  const end = new Date(date);
  end.setMilliseconds(1);
  return end;
};

/**
 * 1ヶ月分のスケジュールデータから、カレンダー表示用のイベント一覧を組み立てる.
 *
 * 口座振替の引落日は「月末日（非営業日の場合は翌営業日）」というルール上、
 * 月末が土日の場合に実日付が翌月へ繰り越すことがある。そのため、ここで組み立てた
 * イベントの実日付は必ずしも`data`が表す年月とは限らない。呼び出し側で
 * {@link filterEventsForYearMonth} を使い、実日付が属する月のカードへ振り分けること。
 */
export const buildScheduleEvents = (
  data: BillingPaymentScheduleCalendar
): ScheduleCalendarEvent[] => {
  const events: ScheduleCalendarEvent[] = [];

  if (data.invoiceSendOn) {
    const date = getLocalDateFromDateStr(data.invoiceSendOn);
    events.push({
      title: BillingPaymentScheduleConstants.InvoiceSendOnAbbr,
      fullTitle: BillingPaymentScheduleConstants.InvoiceSendOn,
      start: date,
      end: toEventEnd(date),
      allDay: true,
      type: "invoice",
    });
  }
  if (data.settlementType && data.settlementOn) {
    const date = getLocalDateFromDateStr(data.settlementOn);
    events.push({
      title: settlementLabelAbbr(data.settlementType),
      fullTitle: settlementLabel(data.settlementType),
      start: date,
      end: toEventEnd(date),
      allDay: true,
      type: "settlement",
    });
  }
  if (data.paymentNoticeSendOn) {
    const date = getLocalDateFromDateStr(data.paymentNoticeSendOn);
    events.push({
      title: BillingPaymentScheduleConstants.PaymentNoticeSendOnAbbr,
      fullTitle: BillingPaymentScheduleConstants.PaymentNoticeSendOn,
      start: date,
      end: toEventEnd(date),
      allDay: true,
      type: "paymentNotice",
    });
  }
  if (data.outsourcingFeePaymentOn) {
    const date = getLocalDateFromDateStr(data.outsourcingFeePaymentOn);
    events.push({
      title: BillingPaymentScheduleConstants.OutsourcingFeePaymentOnAbbr,
      fullTitle: BillingPaymentScheduleConstants.OutsourcingFeePaymentOn,
      start: date,
      end: toEventEnd(date),
      allDay: true,
      type: "outsourcingFeePayment",
    });
  }

  return events;
};

/**
 * イベントの実日付が指定した年月（yyyyMM）に属するものだけを抽出する.
 *
 * 営業日調整により日付が翌月へ繰り越した場合でも、実日付が属する月のカードに
 * 正しく表示されるようにするため、呼び出し元は表示中の全月分のイベントを
 * まとめてこの関数に渡し、カードごとの年月でフィルタする。
 */
export const filterEventsForYearMonth = (
  events: ScheduleCalendarEvent[],
  yearMonth: string
): ScheduleCalendarEvent[] => {
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(4, 6)) - 1;
  return events.filter(
    (event) =>
      event.start.getFullYear() === year && event.start.getMonth() === month
  );
};

/**
 * イベントの実日付が指定した年月（yyyyMM）に属さないものだけを抽出する（{@link filterEventsForYearMonth}の逆条件）.
 *
 * 月末日等の非営業日調整により、実日付が本来の年月から翌月へ繰り越したイベントを判定するために使う.
 */
export const filterEventsOutsideYearMonth = (
  events: ScheduleCalendarEvent[],
  yearMonth: string
): ScheduleCalendarEvent[] => {
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(4, 6)) - 1;
  return events.filter(
    (event) =>
      !(event.start.getFullYear() === year && event.start.getMonth() === month)
  );
};

// カレンダー外の注釈1件分（カレンダー枠内と同じ短縮名称のバッジ＋実日付。例: [口座振替] 2027年1月3日）
export type OverflowNotice = {
  type: EventType;
  // カレンダー枠内・凡例と同じ短縮名称（バッジに表示する）
  abbr: string;
  // 実日付の表記（例: "2027年1月3日"）
  dateLabel: string;
};

/**
 * 本来の年月のカード自体には表示できない繰り越しイベントを、カレンダー外の注釈に変換する.
 */
export const buildOverflowNotices = (
  events: ScheduleCalendarEvent[]
): OverflowNotice[] =>
  events.map((event) => ({
    type: event.type,
    abbr: event.title,
    dateLabel: formatDateJA(event.start),
  }));
